import type { App } from '@grafikr/components';

import intersectionLoader from 'utils/loaders/intersection';
import onEvent from 'utils/loaders/on-event';
import onElementInteraction from 'utils/loaders/on-element-interaction';
import hasUrlParam from 'utils/loaders/has-url-param';
import cartCount from 'components/cart-count';
import tabs from 'components/tabs';
import clubSignupDrawer from 'components/club-signup-drawer';
import clubSignupForm from 'components/club-signup-form';

export default (app: App) => {
  app.add({
    // Layout
    body: [[intersectionLoader], () => import(/* webpackChunkName: "layout-body" */ 'layout/body')],
    'live-search': [
      [onElementInteraction],
      () => import(/* webpackChunkName: "component-live-search" */ 'layout/live-search'),
    ],
    header: [
      [intersectionLoader],
      () => import(/* webpackChunkName: "layout-header" */ 'layout/header'),
    ],

    // Components
    'app-form': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-app-form" */ 'components/app-form'),
    ],
    announcements: [
      intersectionLoader,
      () =>
        import(/* webpackChunkName: "component-announcements" */ 'components/header-announcements'),
    ],
    navigation: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-navigation" */ 'components/header-navigation'),
    ],
    'address-form': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-address-form" */ 'components/address-form'),
    ],
    'mobile-menu': [
      [onEvent, onElementInteraction],
      () => import(/* webpackChunkName: "component-mobile-menu" */ 'components/header-mobile-menu'),
    ],
    'mobile-menu-featured-collections': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-mobile-menu-featured-collections" */ 'components/header-mobile-menu-featured-collections'
        ),
    ],
    'cart-drawer': [
      [onEvent, onElementInteraction, hasUrlParam],
      () => import(/* webpackChunkName: "component-cart-drawer" */ 'components/cart-drawer'),
    ],
    'cart-count': cartCount,
    'club-signup-drawer': clubSignupDrawer,
    'club-signup-form': clubSignupForm,
    copy: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-copy" */ 'components/copy'),
    ],
    'lazy-content': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-lazy-content" */ 'components/lazy-content'),
    ],
    accordion: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-accordion" */ 'components/accordion'),
    ],
    'featured-items': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-featured-items" */ 'components/featured-items'),
    ],
    'media-feature': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-media-feature" */ 'components/media-feature'),
    ],
    'store-info': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-store-info" */ 'components/store-info'),
    ],
    'featured-collection': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-featured-collection" */ 'components/featured-collection'
        ),
    ],
    'background-color': [
      intersectionLoader,
      () =>
        import(/* webpackChunkName: "component-background-color" */ 'components/background-color'),
    ],
    'quick-atc': [
      [intersectionLoader, onEvent, onElementInteraction],
      () => import(/* webpackChunkName: "component-quick-atc" */ 'components/quick-atc'),
    ],
    'price-match': [
      [onEvent, onElementInteraction],
      () => import(/* webpackChunkName: "component-price-match" */ 'components/price-match'),
    ],
    tabs,
    video: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-video" */ 'components/video'),
    ],
    'upsell-item': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-upsell-item" */ 'components/upsell-item'),
    ],
    'price-match-popup': [
      [intersectionLoader, onElementInteraction],
      () =>
        import(
          /* webpackChunkName: "component-price-match-popup" */ 'components/price-match-popup'
        ),
    ],
    'slider-grid': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-slider-grid" */ 'components/slider-grid'),
    ],
    'youtube-modal': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-youtube-modal" */ 'components/youtube-modal'),
    ],
    tooltip: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-tooltip" */ 'components/tooltip'),
    ],

    // Pages
    product: [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-main" */ 'pages/product/main'),
    ],
    'product-form': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-form" */ 'pages/product/form'),
    ],
    upsell: [
      intersectionLoader,
      () => import(/* webpackChunkName: "upsell" */ 'pages/product/upsell'),
    ],
    'product-recommendations': [
      intersectionLoader,
      () =>
        import(/* webpackChunkName: "product-recommendations" */ 'pages/product/recommendations'),
    ],
    'product-images': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-images" */ 'pages/product/images'),
    ],
    'size-guide': [
      [onEvent, onElementInteraction],
      () => import(/* webpackChunkName: "component-size-guide" */ 'components/size-guide'),
    ],
    'sticky-atc': [
      intersectionLoader,
      () => import(/* webpackChunkName: "sticky-atc" */ 'pages/product/sticky-atc'),
    ],
    'inventory-status': [
      [onEvent, onElementInteraction],
      () => import(/* webpackChunkName: "inventory-status" */ 'pages/product/inventory-status'),
    ],
    'add-to-cart-upsell': [
      [onEvent],
      () => import(/* webpackChunkName: "add-to-cart-upsell" */ 'pages/product/add-to-cart-upsell'),
    ],
    'add-to-cart-upsell-bulk': [
      [onEvent],
      () =>
        import(
          /* webpackChunkName: "add-to-cart-upsell-bulk" */ 'pages/product/add-to-cart-upsell-bulk'
        ),
    ],
    'back-in-stock': [
      [onEvent, onElementInteraction],
      () => import(/* webpackChunkName: "back-in-stock" */ 'pages/product/back-in-stock'),
    ],
    'product-bundle-options': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-bundle-options" */ 'pages/product/bundle-options'),
    ],
    'product-bulk-discount': [
      [intersectionLoader],
      () => import(/* webpackChunkName: "product-bulk-discount" */ 'pages/product/bulk-discount'),
    ],
    'simple-bundles': [
      intersectionLoader,
      () => import(/* webpackChunkName: "simple-bundles" */ 'pages/product/simple-bundles'),
    ],

    collection: [
      intersectionLoader,
      () => import(/* webpackChunkName: "collection-main" */ 'pages/collection/main'),
    ],

    blog: [intersectionLoader, () => import(/* webpackChunkName: "blog-main" */ 'pages/blog/main')],

    article: [
      intersectionLoader,
      () => import(/* webpackChunkName: "article-main" */ 'pages/blog/article'),
    ],

    search: [
      intersectionLoader,
      () => import(/* webpackChunkName: "search-main" */ 'pages/search/main'),
    ],

    'search-categories': [
      intersectionLoader,
      () => import(/* webpackChunkName: "search-categories" */ 'components/search-categories'),
    ],

    'search-articles': [
      [intersectionLoader, onEvent],
      () => import(/* webpackChunkName: "search-articles" */ 'components/search-articles'),
    ],
  });
};
